<template>
  <v-menu
    v-model="menuOpen"
    offset-y
    left
    :close-on-content-click="false"
    max-width="380"
    min-width="340"
  >
    <template #activator="{ on: menuOn, attrs: menuAttrs }">
      <v-tooltip bottom>
        <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <v-btn
            icon
            class="bright--text"
            :aria-label="$t('notification.title')"
            v-bind="{ ...menuAttrs, ...tooltipAttrs }"
            v-on="{ ...menuOn, ...tooltipOn }"
          >
            <v-badge
              :content="unreadCount"
              :value="unreadCount > 0"
              color="red"
              overlap
            >
              <v-icon>mdi-bell-outline</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <span>{{ $t('notification.title') }}</span>
      </v-tooltip>
    </template>

    <v-card>
      <div
        class="notif-header d-flex align-center justify-space-between px-4 py-3"
      >
        <span class="text-subtitle-1 font-weight-medium">
          {{ $t('notification.title') }}
        </span>
        <v-btn
          v-if="unreadCount > 0"
          text
          x-small
          color="primary"
          @click="handleMarkAllRead"
        >
          {{ $t('notification.markAllRead') }}
        </v-btn>
      </div>

      <v-divider />

      <v-list v-if="list.length > 0" dense class="notif-list pa-0">
        <v-list-item
          v-for="item in list"
          :key="item.id"
          class="notif-item"
          :class="{ 'notif-unread': !item.isRead }"
          @click="handleItemClick(item)"
        >
          <v-list-item-icon class="mr-3 my-auto">
            <v-icon :color="getTypeColor(item.type)" size="22">
              {{ getTypeIcon(item.type) }}
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="text-body-2">
              {{ item.title }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-caption">
              {{ item.message }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action class="my-auto ml-2">
            <div class="d-flex align-center">
              <span class="text-caption grey--text">
                {{ formatTimeAgo(item.createdAt) }}
              </span>
              <div v-if="!item.isRead" class="unread-dot ml-2" />
            </div>
          </v-list-item-action>
        </v-list-item>
      </v-list>

      <div v-else class="text-center py-8">
        <v-icon size="40" color="grey lighten-1">mdi-bell-check-outline</v-icon>
        <div class="text-body-2 grey--text mt-2">
          {{ $t('notification.empty') }}
        </div>
      </div>

      <v-divider />
      <div class="text-center py-2">
        <v-btn text small color="primary" @click="goToNotifications">
          {{ $t('notification.viewAll') }}
        </v-btn>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
const TYPE_CONFIG = {
  login: { icon: 'mdi-login', color: 'blue' },
  withdraw_approved: { icon: 'mdi-check-circle-outline', color: 'green' },
  withdraw_rejected: { icon: 'mdi-close-circle-outline', color: 'red' },
  system: { icon: 'mdi-information-outline', color: 'orange' },
}
const DEFAULT_CONFIG = { icon: 'mdi-bell-outline', color: 'grey' }

export default {
  name: 'NotificationBell',

  data() {
    return {
      menuOpen: false,
    }
  },

  computed: {
    list() {
      return this.$store.state.notification.list
    },
    unreadCount() {
      return this.$store.state.notification.unreadCount
    },
  },

  watch: {
    menuOpen(open) {
      if (open) {
        this.$store.dispatch('notification/fetchList', { offset: 0, limit: 10 })
      }
    },
  },

  methods: {
    getTypeIcon(type) {
      return (TYPE_CONFIG[type] || DEFAULT_CONFIG).icon
    },
    getTypeColor(type) {
      return (TYPE_CONFIG[type] || DEFAULT_CONFIG).color
    },
    handleItemClick(item) {
      if (!item.isRead) {
        this.$store.dispatch('notification/markRead', item.id)
      }
    },
    handleMarkAllRead() {
      this.$store.dispatch('notification/markAllRead')
    },
    goToNotifications() {
      this.menuOpen = false
      this.$router.push('/notification_preferences').catch(() => {})
    },
    formatTimeAgo(dateStr) {
      if (!dateStr) return ''
      const now = Date.now()
      // Server returns UTC without timezone suffix — append 'Z' so browser parses as UTC
      const normalized =
        dateStr.endsWith('Z') || dateStr.includes('+') || dateStr.includes('T')
          ? dateStr
          : dateStr.replace(' ', 'T') + 'Z'
      const date = new Date(normalized).getTime()
      const diff = Math.floor((now - date) / 1000)

      if (diff < 60) return this.$t('notification.timeAgo.justNow')
      if (diff < 3600)
        return this.$t('notification.timeAgo.minutesAgo', {
          n: Math.floor(diff / 60),
        })
      if (diff < 86400)
        return this.$t('notification.timeAgo.hoursAgo', {
          n: Math.floor(diff / 3600),
        })
      if (diff < 604800)
        return this.$t('notification.timeAgo.daysAgo', {
          n: Math.floor(diff / 86400),
        })
      return new Date(dateStr).toLocaleDateString()
    },
  },
}
</script>

<style scoped>
.notif-header {
  min-height: 48px;
}
.notif-list {
  max-height: 400px;
  overflow-y: auto;
}
.notif-item {
  min-height: 56px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.notif-unread {
  background: rgba(33, 150, 243, 0.04);
}
.theme--dark .notif-unread {
  background: rgba(33, 150, 243, 0.08);
}
.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2196f3;
  flex-shrink: 0;
}
</style>
